import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  Slide,
  Select,
  Checkbox,
  Avatar,
  FormControl,
  OutlinedInput,
  Box,
  Chip,
  InputLabel,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { db, functions } from '../service/Firebase-config';
import { collection, getDocs, query, where } from '@firebase/firestore';
import LoadingButton from '@mui/lab/LoadingButton';
import { httpsCallable } from 'firebase/functions';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function DialogNew({ open, handleCancel, isStudent }) {
  const grades = [
    'Grade 1',
    'Grade 2',
    'Grade 3',
    'Grade 4',
    'Grade 5',
    'Grade 6',
    'Grade 7',
    'Grade 8',
    'Grade 9',
    'Grade 10',
    'Grade 11',
    'Grade 12',
    'University',
  ];

  const [unassignTeachers, setUnassignTeachers] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setPassword(value);
    setPasswordError(value.length < 6);
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    setEmailError('');
  };

  useEffect(() => {
    const fetchTeachers = async () => {
      const q = query(
        collection(db, 'teachers'),
        where('active', '==', true)
      );

      const teacherCollection = await getDocs(q);

      const teachersList = teacherCollection.docs.map((doc) => ({
        uid: doc.id,
        ...doc.data(),
      }));

      setUnassignTeachers(teachersList);
    };

    if (open && isStudent) {
      fetchTeachers();
    } else {
      setUnassignTeachers([]);
      setSelectedTeachers([]);
      setIsLoading(false);
    }

    if (open) {
      setPassword('');
      setPasswordError(false);
      setEmail('');
      setEmailError('');
    }
  }, [isStudent, open]);

  const handleTeacherChange = (event) => {
    setSelectedTeachers(event.target.value);
  };

  const handleCreate = async (newUser) => {
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setEmailError('Please enter an email.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    try {
      setIsLoading(true);
      setEmailError('');

      const checkEmailDuplicate = httpsCallable(
        functions,
        'checkEmailDuplicate'
      );

      const result = await checkEmailDuplicate({
        email: trimmedEmail,
      });

      const data = result.data;

      if (data.duplicated) {
        setEmailError(
          data.message || 'This email is already in use.'
        );
        return;
      }

      newUser.email = trimmedEmail;
      newUser.isStudent = isStudent;

      if (isStudent) {
        const selectedValues = !!newUser.teacherList
          ? newUser.teacherList.split(',')
          : null;

        newUser.teacherList = selectedValues;
      }

      const createUser = httpsCallable(
        functions,
        'handleCreateUser'
      );

      await createUser(newUser).catch((e) => console.log(e));

      handleCancel();
    } catch (error) {
      console.error(error);
      setEmailError('Failed to check email duplication.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      PaperProps={{
        component: 'form',
        onSubmit: (event) => {
          event.preventDefault();

          const formData = new FormData(event.currentTarget);
          const formJson = Object.fromEntries(formData.entries());

          handleCreate(formJson);
        },
      }}
      disableEscapeKeyDown
      disableBackdropClick
    >
      <DialogTitle>
        {isStudent ? 'Create New Student' : 'Create New Teacher'}
      </DialogTitle>

      <DialogContent>
        <DialogContentText>
          <TextField
            required
            margin="dense"
            id="email"
            name="email"
            label="Email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError}
          />

          <TextField
            margin="dense"
            id="password"
            name="password"
            label="New Password"
            fullWidth
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordChange}
            error={passwordError}
            helperText={
              passwordError
                ? 'Password must be at least 6 characters long.'
                : ''
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            required
            margin="dense"
            id="displayName"
            name="displayName"
            label="Full Name"
            fullWidth
            variant="outlined"
          />

          <TextField
            margin="dense"
            id="phone"
            name="phone"
            label="Phone Number"
            fullWidth
            variant="outlined"
          />

          {isStudent && (
            <>
              <TextField
                required
                margin="dense"
                id="parentPhone"
                name="parentPhone"
                label="Parent Phone Number"
                fullWidth
                variant="outlined"
              />

              <TextField
                required
                margin="dense"
                id="school"
                name="school"
                label="School"
                fullWidth
                variant="outlined"
              />

              <TextField
                required
                select
                fullWidth
                margin="dense"
                id="grade"
                name="grade"
                label="Grade"
                defaultValue={grades[0]}
              >
                {grades &&
                  grades.map((gradeItem) => (
                    <MenuItem
                      key={gradeItem}
                      value={gradeItem}
                    >
                      {gradeItem}
                    </MenuItem>
                  ))}
              </TextField>

              <FormControl
                fullWidth
                variant="outlined"
                sx={{ mt: 1, mb: 1 }}
              >
                <InputLabel id="teacherList-label">
                  Assign Teachers
                </InputLabel>

                <Select
                  labelId="teacherList-label"
                  id="teacherList"
                  name="teacherList"
                  multiple
                  value={selectedTeachers}
                  onChange={handleTeacherChange}
                  input={
                    <OutlinedInput label="Assign Teachers" />
                  }
                  renderValue={(selected) => (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                      }}
                    >
                      {selected &&
                        selected.map((value) => {
                          const teacher =
                            unassignTeachers.find(
                              (ut) => ut.uid === value
                            );

                          return (
                            <Chip
                              key={value}
                              label={teacher.displayName}
                              avatar={
                                <Avatar
                                  alt={teacher.displayName}
                                  src={teacher.photoUrl}
                                />
                              }
                            />
                          );
                        })}
                    </Box>
                  )}
                >
                  {unassignTeachers &&
                    unassignTeachers.map((teacher) => (
                      <MenuItem
                        key={teacher.uid}
                        value={teacher.uid}
                      >
                        <Checkbox
                          checked={selectedTeachers.includes(
                            teacher.uid
                          )}
                        />

                        <Avatar
                          sx={{ mr: 1 }}
                          alt={teacher.displayName}
                          src={teacher.photoUrl}
                        />

                        {teacher.displayName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </>
          )}

          <TextField
            required
            margin="dense"
            id="branch"
            name="branch"
            label="branch"
            fullWidth
            variant="outlined"
            defaultValue={'main'}
          />

          <TextField
            margin="dense"
            id="info"
            name="info"
            label="Info"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
          />
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <LoadingButton
          loading={isLoading}
          autoFocus
          type="submit"
        >
          Save
        </LoadingButton>

        <Button onClick={handleCancel}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}